<template>
  <ButtonSquare
    :title="$t('general__view__outdoorview')"
    :imgSource="iconIndoorview"
    size="large"
    :activeState="activeState"
    :hoverButtons="hoverButtons"
    :hoverExplanationText="
      activeState === 'deactivated' && isIndoorviewShowing
        ? $t('general__text__loading')
        : 'undefined'
    "
    :activeHoverButtonValue="activeInstanceNumber"
    @clicked="handleOutdoorviewButtonClick"
    @onHoverButtonClick="changeInstance"
    @mouseenter="onHover"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from 'vuex'
import ButtonSquare, { Button } from '@/components/general/buttons/ButtonSquare/ButtonSquare.vue'
import { INDOORVIEWER_PREFIX, MAPVIEW_PREFIX } from '@/store/module-prefixes'
import { CHANGE_PRIMARY_INSTANCE, CHANGE_SECONDARY_INSTANCE } from '@/store/mutation-types'
import { setViewToIndoorview, setViewToMapview } from '@/utils/views'
import { useGeneralStore } from '@/pinia'
import { zoomToEntities } from '@/composables/useMapbox'
import iconIndoorview from '@/assets/icon-indoorview.svg'

// Props and emits
const props = defineProps<{
  disabled?: boolean
  viewerNumber?: number
}>()
const emit = defineEmits(['SelectRealEstateIvionButtonHovered'])

// Vuex and Pinia stores
const store = useStore()
const piniaStore = useGeneralStore()

// State and computed
const instanceLinks = computed(() => store.state[INDOORVIEWER_PREFIX].instanceLinks)
const primaryInstance = computed(() => store.state[INDOORVIEWER_PREFIX].primaryInstance)
const secondaryInstance = computed(() => store.state[INDOORVIEWER_PREFIX].secondaryInstance)
const selectedRealEstate = computed(() => store.state[MAPVIEW_PREFIX].selectedRealEstate)
const allRealEstates = computed(() => store.getters.allRealEstates)
const isIndoorviewShowing = computed(() => store.getters.isIndoorviewShowing)

// Compute active state for the button
const activeState = computed(() => {
  if (!hoverButtons.value.length || props.disabled) return 'deactivated'
  return isIndoorviewShowing.value ? 'active' : 'inactive'
})

// Compute hover buttons
const hoverButtons = computed<Button[]>(() => {
  return instanceLinks.value.map((link: any, index: number) => {
    const title = link.title ?? `Site ${index + 1}`
    return {
      title,
      emit: index.toString()
    }
  })
})

// Compute active instance number
const activeInstanceNumber = computed(() => {
  if ((props.viewerNumber ?? 1) === 1)
    return primaryInstance.value ? primaryInstance.value.toString() : '0'
  return secondaryInstance.value ? secondaryInstance.value.toString() : '0'
})

// Mutations
function changePrimaryInstance(instanceNr: number) {
  store.commit(`${INDOORVIEWER_PREFIX}/${CHANGE_PRIMARY_INSTANCE}`, instanceNr)
}
function changeSecondaryInstance(instanceNr: number) {
  store.commit(`${INDOORVIEWER_PREFIX}/${CHANGE_SECONDARY_INSTANCE}`, instanceNr)
}

// Handle main button click
async function handleOutdoorviewButtonClick() {
  if (!instanceLinks.value.length || props.disabled) return
  if (!isIndoorviewShowing.value) {
    piniaStore.setShowFeature('featureInfo')
    setViewToIndoorview()
    return
  }
  setViewToMapview()
  const realEstate = allRealEstates.value.find(
    (realEstate: any) => realEstate.id === selectedRealEstate.value.id
  )
  if (!realEstate) return
  zoomToEntities([realEstate.id])
}

// Hover event
function onHover() {
  if (activeState.value !== 'deactivated') {
    emit('SelectRealEstateIvionButtonHovered')
  }
}

// Change instance handler
function changeInstance(instanceNumber: string) {
  if (!isIndoorviewShowing.value) {
    piniaStore.setShowFeature('featureInfo')
  }
  if (instanceNumber === 'Missing data link') {
    return
  }
  if ((props.viewerNumber ?? 1) === 1) {
    if (primaryInstance.value !== instanceNumber) {
      const instanceNr = parseInt(instanceNumber)
      changePrimaryInstance(instanceNr)
    }
  } else if (secondaryInstance.value !== instanceNumber) {
    const instanceNr = parseInt(instanceNumber)
    changeSecondaryInstance(instanceNr)
  }
  if (!isIndoorviewShowing.value) setViewToIndoorview()
}
</script>
